import { useState, useEffect, useCallback, useRef } from 'react';

interface AutoSaveOptions {
  /** Key to use for localStorage */
  key: string;
  /** Interval in milliseconds (default: 30000 = 30s) */
  interval?: number;
  /** Whether auto-save is enabled (default: true) */
  enabled?: boolean;
  /** Callback when data is saved */
  onSave?: (data: any) => void;
  /** Callback when data is restored */
  onRestore?: (data: any) => void;
}

interface AutoSaveResult<T> {
  /** Current data */
  data: T;
  /** Update data */
  setData: (data: T | ((prev: T) => T)) => void;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Whether there's a draft available */
  hasDraft: boolean;
  /** Restore draft from localStorage */
  restoreDraft: () => T | null;
  /** Clear draft from localStorage */
  clearDraft: () => void;
  /** Manually save current data */
  saveNow: () => void;
  /** Whether data has changed since last save */
  isDirty: boolean;
}

/**
 * Hook for auto-saving data to localStorage
 * 
 * @example
 * ```tsx
 * const { data, setData, lastSaved, hasDraft, restoreDraft, clearDraft } = useAutoSave({
 *   key: 'template-draft-123',
 *   interval: 30000,
 *   enabled: true,
 * });
 * ```
 */
export function useAutoSave<T>(
  initialData: T,
  options: AutoSaveOptions
): AutoSaveResult<T> {
  const {
    key,
    interval = 30000,
    enabled = true,
    onSave,
    onRestore,
  } = options;

  const [data, setDataInternal] = useState<T>(initialData);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const initialDataRef = useRef(initialData);
  const dataRef = useRef(data);

  // Keep dataRef in sync
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Storage key with prefix
  const storageKey = `autosave-${key}`;

  // Check if draft exists
  const hasDraft = useCallback(() => {
    try {
      return localStorage.getItem(storageKey) !== null;
    } catch {
      return false;
    }
  }, [storageKey]);

  // Save to localStorage
  const saveToStorage = useCallback((dataToSave: T) => {
    try {
      const payload = {
        data: dataToSave,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(storageKey, JSON.stringify(payload));
      setLastSaved(new Date());
      setIsDirty(false);
      onSave?.(dataToSave);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [storageKey, onSave]);

  // Restore from localStorage
  const restoreDraft = useCallback((): T | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { data: restoredData, timestamp } = JSON.parse(stored);
        setDataInternal(restoredData);
        setLastSaved(new Date(timestamp));
        setIsDirty(false);
        onRestore?.(restoredData);
        return restoredData;
      }
    } catch (error) {
      console.error('Failed to restore draft:', error);
    }
    return null;
  }, [storageKey, onRestore]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setLastSaved(null);
      setIsDirty(false);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, [storageKey]);

  // Manual save
  const saveNow = useCallback(() => {
    saveToStorage(dataRef.current);
  }, [saveToStorage]);

  // Update data and mark as dirty
  const setData = useCallback((newData: T | ((prev: T) => T)) => {
    setDataInternal((prev) => {
      const updated = typeof newData === 'function' 
        ? (newData as (prev: T) => T)(prev) 
        : newData;
      setIsDirty(true);
      return updated;
    });
  }, []);

  // Auto-save interval
  useEffect(() => {
    if (!enabled || !isDirty) return;

    const timer = setInterval(() => {
      if (isDirty) {
        saveToStorage(dataRef.current);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [enabled, interval, isDirty, saveToStorage]);

  // Save on unmount if dirty
  useEffect(() => {
    return () => {
      if (isDirty && enabled) {
        saveToStorage(dataRef.current);
      }
    };
  }, []);

  return {
    data,
    setData,
    lastSaved,
    hasDraft: hasDraft(),
    restoreDraft,
    clearDraft,
    saveNow,
    isDirty,
  };
}

/**
 * Hook for managing form drafts with auto-save
 */
export function useFormDraft<T extends Record<string, any>>(
  formId: string,
  initialValues: T,
  options?: Omit<AutoSaveOptions, 'key'>
) {
  const {
    data: formData,
    setData: setFormData,
    lastSaved,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveNow,
    isDirty,
  } = useAutoSave(initialValues, {
    key: `form-${formId}`,
    ...options,
  });

  // Update a single field
  const setField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, [setFormData]);

  // Update multiple fields
  const setFields = useCallback((fields: Partial<T>) => {
    setFormData((prev) => ({ ...prev, ...fields }));
  }, [setFormData]);

  // Reset to initial values
  const reset = useCallback(() => {
    setFormData(initialValues);
    clearDraft();
  }, [initialValues, setFormData, clearDraft]);

  return {
    values: formData,
    setField,
    setFields,
    setValues: setFormData,
    lastSaved,
    hasDraft,
    restoreDraft,
    clearDraft,
    saveNow,
    isDirty,
    reset,
  };
}

export default useAutoSave;
